import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { TextSubHeading, TextCallout2, Button, BankIdLogo, colors } from '@blocket/blocket-ui'
import { actionCreators as authActionCreators } from '@blocket/authentication-fe'
import { chatVerificationPromptView, chatVerificationPromptEngagement } from 'tracking/chat'
import SHARED_FILES_URL from 'constants/sharedFilesUrl'
import { SMS_AND_BANKID, BANKID, SMS } from 'constants/verificationTypes'
import useOnMount from 'customHooks/useOnMount'
import { awaitPulseTracking } from 'utils/pulse'

const Wrapper = styled.div`
  display: flex;
  background: ${colors.blue50};
  padding: 16px 24px;
`

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const IconWrapper = styled.div`
  display: flex;
  margin-right: 8px;
  align-items: center;
`

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  align-items: flex-start;
`

const VerifyButton = styled(Button)`
  width: 100%;
  max-width: ${({ isMobile }) => (isMobile ? 'inherit' : '210px')};
  &:first-child {
    margin-right: 12px;
    margin-bottom: 16px;
  }
`

const Illustration = styled.div`
  ${({ size }) => size && `width: ${size}px`};
  ${({ size }) => size && `height: ${size}px`};
  background-image: url('${SHARED_FILES_URL}/illustrations/verified_mobile_white_bg.svg');
  background-size: cover;
  background-repeat: no-repeat;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`

const TextCallout = styled(TextCallout2)`
  margin-bottom: ${({ isMobile }) => (isMobile ? '16px' : '8px')};
`

const Text = ({ type, isMobile }) => {
  const text =
    type === BANKID
      ? 'För att skicka meddelanden behöver du verifiera din inloggade session.'
      : type === SMS_AND_BANKID &&
        'Från 1 juni kommer du bara att kunna verifiera dig med BankID. Gör det redan nu för att kunna fortsätta använda Blocket som vanligt'
  return <TextCallout isMobile={isMobile}>{text}</TextCallout>
}

const VerificationPrompt = ({ type, isMobile, selectedChannel }) => {
  const iconSize = isMobile ? 32 : 40
  const router = useRouter()
  const dispatch = useDispatch()

  const redirectToVerification = (verificationOption) => {
    dispatch(
      authActionCreators.blocketLoginHelper.goToVerification(
        router.asPath,
        router.asPath,
        'Chat',
        verificationOption,
      ),
    )
  }

  const handleClick = ({ type }) =>
    awaitPulseTracking(
      () => chatVerificationPromptEngagement({ type, selectedChannel }),
      () => redirectToVerification(type),
    )

  useOnMount(() => {
    chatVerificationPromptView({ type, selectedChannel })
  })

  return (
    <Wrapper>
      <InnerWrapper>
        <Header>
          <IconWrapper>
            {type === BANKID && <BankIdLogo width={iconSize} height={iconSize} />}
            {type === SMS_AND_BANKID && <Illustration size={iconSize} />}
          </IconWrapper>
          <TextSubHeading>Verifiera dig för att fortsätta</TextSubHeading>
        </Header>
        <Text type={type} isMobile={isMobile} />
        <ButtonWrapper isMobile={isMobile}>
          <VerifyButton
            size="small"
            type="primary"
            onClick={() => handleClick({ type: BANKID })}
            isMobile={isMobile}
          >
            Verifiera med BankID
          </VerifyButton>
          {type === SMS_AND_BANKID && (
            <VerifyButton
              size="small"
              type="secondary"
              onClick={() => handleClick({ type: SMS })}
              isMobile={isMobile}
            >
              Verifiera med SMS
            </VerifyButton>
          )}
        </ButtonWrapper>
      </InnerWrapper>
    </Wrapper>
  )
}

VerificationPrompt.propTypes = {
  // Required verification type.
  type: PropTypes.string,
  isMobile: PropTypes.bool,
  selectedChannel: PropTypes.object.isRequired,
}

export default VerificationPrompt
